import React, { ReactNode } from 'react';
import { ThemeConsumer, ThemeProvider } from 'styled-components';
import useWindowWidth from '../../../hooks/useWindowWidth';
import MultiActions from './MultiActions/MultiActions';
import PrimaryAction from './PrimaryAction/PrimaryAction';
import { PrimaryActionType, SecondaryActionType, TertiaryActionType } from '../Block';
import { BlockFooterWrapper } from './styles';

export interface BlockFooterProps {
  actionText?: ReactNode;
  actionType?: 'danger' | 'primary';
  actionSize?: 'wide' | 'full';
  actionCallback?: () => void;
  actionIsLoading?: boolean;
  primaryAction?: PrimaryActionType;
  secondaryAction?: SecondaryActionType;
  tertiaryAction?: TertiaryActionType;
  formId?: string | undefined;
  'data-testid'?: string | number;
}

/*
  Notes:

  1. If the Block is within a Modal
    - Having a single action will render a `full` primary Button
    - Having > 1 actions will render a `wide` primary button
*/

function BlockFooter(props: BlockFooterProps) {
  const {
    actionCallback,
    actionIsLoading,
    actionSize,
    actionText,
    actionType,
    primaryAction,
    secondaryAction,
    tertiaryAction,
    formId,
  } = props;

  const { isMobile } = useWindowWidth();

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        mobile: isMobile,
      })}
    >
      <BlockFooterWrapper data-testid={props['data-testid']}>
        {primaryAction?.label && secondaryAction?.label ? (
          <MultiActions
            actionIsLoading={actionIsLoading}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            tertiaryAction={tertiaryAction}
            formId={formId}
          />
        ) : (
          <ThemeConsumer>
            {theme => (
              <PrimaryAction
                {...primaryAction}
                formId={formId}
                label={actionText || primaryAction?.label}
                type={actionType || primaryAction?.type}
                callback={actionCallback || primaryAction?.callback}
                actionIsLoading={actionIsLoading}
                size={theme && theme.isModal ? 'full' : actionSize || primaryAction?.size}
              />
            )}
          </ThemeConsumer>
        )}
      </BlockFooterWrapper>
    </ThemeProvider>
  );
}

BlockFooter.defaultProps = {
  actionSize: 'wide',
};

export default BlockFooter;
